// ============================================================
// HOUSE OF CARDS — Inventory v3 — DRAWN ITEMS
// Items are little cards with their HAND-DRAWN icon in the art
// slot (the red ball, the moon-keys, the gold bar), a rarity-
// coded border (bone / mold / ember / gold / flesh), and an
// equipped glow. Same behavior: click to ARM, click a hotspot
// to use, click again to disarm.
// ============================================================
import React from "react";
import { RM, veinGlow, ITEM_RARITY, RARITY_COLORS } from "./theme";
import { ITEMS } from "./roomData";
import { ITEM_ICONS } from "./HotspotArt";

const monogram = (name = "?") =>
  name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("");

export default function Inventory({ inventory, armedItem, onArm, hidden }) {
  const armedName = armedItem ? ITEMS[armedItem]?.name : null;
  if (hidden) return null;

  return (
    <>
      {armedName && (
        <div style={S.armedHint} className="plaque">
          <span style={{ color: RM.moldBright }}>{armedName}</span> in hand — click a target in the room
        </div>
      )}
      <div style={S.bar} className="pockets-bar">
        <div style={S.title}>POCKETS</div>
        {inventory.length === 0 && (
          <div style={S.empty}>empty — the house notices empty hands</div>
        )}
        {inventory.map((id) => {
          const it = ITEMS[id];
          const armed = armedItem === id;
          const rarity = RARITY_COLORS[ITEM_RARITY[id]] || RM.boneDim;
          const Icon = ITEM_ICONS[id];
          return (
            <div
              key={id}
              onClick={() => onArm(armed ? null : id)}
              title={it?.desc}
              className="hotspot-card plaque"
              style={{
                ...S.item,
                borderColor: armed ? RM.moldBright : rarity,
                boxShadow: armed
                  ? veinGlow(RM.moldBright)
                  : `inset 0 0 12px rgba(0,0,0,0.6), 0 0 8px ${rarity}22`,
              }}
            >
              {/* art slot — drawn icon now; user images can slot here later */}
              <div style={{ ...S.slot, color: rarity, borderColor: `${rarity}55` }}>
                {Icon ? <Icon /> : monogram(it?.name)}
              </div>
              <div style={{ ...S.itemName, color: armed ? RM.moldBright : RM.bone }}>
                {it?.name || id}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

const S = {
  bar: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 16px",
    background: "linear-gradient(180deg, rgba(10,7,5,0.88), rgba(5,3,2,0.96))",
    borderTop: `1px solid ${RM.charBorder}`,
    boxShadow: "0 -14px 30px rgba(0,0,0,0.5)",
    zIndex: 40,
    flexWrap: "nowrap",
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
  },
  title: {
    flex: "0 0 auto",
    fontFamily: RM.fontD,
    fontSize: 12,
    letterSpacing: 4,
    color: RM.boneFaint,
  },
  empty: {
    flex: "0 0 auto",
    fontFamily: RM.font,
    fontStyle: "italic",
    fontSize: 13,
    color: RM.boneFaint,
    whiteSpace: "nowrap",
  },
  item: {
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "6px 12px 6px 6px",
    border: "1px solid",
    borderRadius: 9,
    background: "linear-gradient(180deg, #1a110c, #100a07)",
    fontFamily: RM.font,
    fontSize: 14,
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
    transition: "all 120ms ease",
  },
  slot: {
    width: 34,
    height: 34,
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid",
    borderRadius: 7,
    background: "rgba(0,0,0,0.45)",
    fontFamily: RM.fontT,
    fontWeight: 700,
    fontSize: 13,
    letterSpacing: 1,
  },
  itemName: { lineHeight: 1.1 },
  armedHint: {
    position: "fixed",
    bottom: 68,
    left: "50%",
    transform: "translateX(-50%)",
    padding: "7px 16px",
    background: "rgba(10,7,5,0.92)",
    border: `1px solid ${RM.moldDim}`,
    borderRadius: 8,
    fontFamily: RM.font,
    fontStyle: "italic",
    fontSize: 13,
    color: RM.boneDim,
    zIndex: 41,
    whiteSpace: "nowrap",
  },
};
